import type { Vec2, WallGrid, WallMetrics } from '../types';
import type { ShapeDefinition } from './registry';
import { wallBoundsFor } from './registry';

/**
 * Flat-top hexagons in offset columns.
 *
 * `size` is the panel's overall width, so the circumradius is half of it. Every
 * one of a hexagon's six neighbours sits at the same centre distance, which is
 * what makes the mounting gap exact here: growing that single distance by the
 * gap separates all six shared edges by exactly the gap, in every direction at
 * once.
 */

const SQRT3 = Math.sqrt(3);

/** Distance between the centres of two touching hexagons, plus the gap. */
function neighbourDistance(size: number, gap: number): number {
  return (SQRT3 * size) / 2 + gap;
}

export const hexagonShape: ShapeDefinition = {
  id: 'hexagon',
  name: 'Hexagon',
  description: 'Flat-top hexagons in offset columns, the honeycomb tiling.',
  phase: 2,
  gridHint: 'Odd columns sit half a row lower, so the hexagons interlock.',

  outline(size: number): Vec2[] {
    const radius = size / 2;
    return Array.from({ length: 6 }, (_, i): Vec2 => {
      const angle = (i * Math.PI) / 3;
      return { x: radius * Math.cos(angle), y: radius * Math.sin(angle) };
    });
  },

  layout(column: number, row: number, size: number, gap: number): Vec2 {
    const distance = neighbourDistance(size, gap);
    const pitchY = distance;
    const pitchX = (distance * SQRT3) / 2;

    return {
      x: size / 2 + column * pitchX,
      y: (SQRT3 * size) / 4 + row * pitchY + (column % 2) * (pitchY / 2),
    };
  },

  metrics(grid: WallGrid, size: number, gap: number): WallMetrics {
    const distance = neighbourDistance(size, gap);
    const bounds = wallBoundsFor(hexagonShape, grid, size, gap);

    return {
      columns: grid.columns,
      rows: grid.rows,
      width: bounds.width,
      height: bounds.height,
      pitchX: (distance * SQRT3) / 2,
      pitchY: distance,
      panelSize: size,
    };
  },
};
